import React from "react";
import { Circle, Ellipse, Group, Rect, Text } from "react-konva";
import consts, { TypeCanvasElement, TypeShape } from "shared/consts";
import { defaultShapeStrokeWidth } from "shared/datamodel/schemas/shape";
import PolygonElement from "frontend/utils/PolygonElement";
import GhostConnector from "./elements/connector/ghost-connector";
import { ConnectorInput } from "shared/datamodel/schemas";
import { CardStackFrame } from "./elements/card-stack/card-stack-subelements";
import ShapeCanvasElement from "./elements/shape-element";
import { getDefaultSizeForShape } from "frontend/utils/shape-utils";
import { noop } from "frontend/utils/fn-utils";
import { GhostTable } from "./elements/table/table-ghost";
import GanttElementComponent from "elements/gantt/gantt-component";
import GanttGhostController from "elements/gantt/controllers/ghost-dummy-controller";
import { createEmptyContext } from "elements/utils";
import { GhostNounIcon } from "./elements/noun-icon/noun-icon-ghost";
import ChartGhostController from "elements/chart/ghost-controller";
import ChartElementComponent from "elements/chart/chart-component";
import IconElementComponent from "elements/icon/icon-component";
import IconController from "elements/icon/controller";
import TaskElementComponent from "elements/task/components/task-component";
import TaskController from "elements/task/controller";
import { Sizes } from "frontend/data/shapes/sizes";
import { useIsCIMReorderEnabled } from "utils/url-utils";

export const GhostElementId = "ghost-element";

export function GhostCanvasElement({
  elementType,
  point1,
  point2,
  data,
}: {
  elementType: TypeCanvasElement;
  point1: { x: number; y: number; rotation?: number };
  point2: { x: number; y: number; rotation?: number };
  data?: any;
}) {
  const isCIM = useIsCIMReorderEnabled();
  const centerX = (point1.x + point2.x) / 2;
  const centerY = (point1.y + point2.y) / 2;
  const sizes = Sizes.get();

  function renderGhostShapeByShapeType() {
    const { color, border } = data.shape;
    const type = data.shape.type as TypeShape;
    const shapeDefaultSize = getDefaultSizeForShape(type, data.shape.subtype);
    let width = Math.abs(point1.x - point2.x),
      height = Math.abs(point1.y - point2.y);
    if (width < consts.MIN_SIZE_NEW_SHAPES || height < consts.MIN_SIZE_NEW_SHAPES) {
      width = shapeDefaultSize.width * sizes.shape.scaleModify;
      height = shapeDefaultSize.height * sizes.shape.scaleModify;
    }
    let scaleX = 1;
    let scaleY = 1;
    if (data.shape.type == consts.CANVAS_ELEMENTS.SHAPE) {
      // TODO: the same calculation are in canvas-stage, before calling placeShape
      const dflt = getDefaultSizeForShape(data.shape.type, data.shape.subtype!); // TODO: can cache this
      const targetSize = { width, height };
      if (data.shape.override?.scaleX) {
        dflt.width /= data.shape.override.scaleX;
      }
      if (data.shape.override?.scaleY) {
        dflt.height /= data.shape.override.scaleY;
      }
      scaleX = targetSize.width / dflt.width;
      scaleY = targetSize.height / dflt.height;
      const rotate = data.shape.override?.rotate ?? 0;
      const fill = data.shape.override?.color ?? data.shape.color;
      const strokeWidth = data.shape.override?.strokeWidth ?? (data.shape.strokeWidth || defaultShapeStrokeWidth);
      return (
        <Group
          id={GhostElementId}
          name="shape"
          x={centerX}
          y={centerY}
          opacity={0.2}
          scaleX={scaleX}
          scaleY={scaleY}
          rotation={rotate}
        >
          <ShapeCanvasElement
            id={GhostElementId}
            element={{ ...data.shape, fill, strokeWidth, scaleX, scaleY }}
            onShapeTypeChanged={noop}
            isFrameHighlighted={false}
          />
        </Group>
      );
    } else {
      const radius = 100 * sizes.baseScaleModify; // the default
      let scaleX = width / (radius * 2);
      let scaleY = height / (radius * 2);
      switch (type) {
        case consts.SHAPES.RECT: {
          return (
            <Rect
              id={GhostElementId}
              width={width}
              height={height}
              name="rect"
              fill={color}
              opacity={0.2}
              shadowBlur={0}
              stroke={border}
              strokeWidth={defaultShapeStrokeWidth}
              x={centerX - width / 2}
              y={centerY - height / 2}
            />
          );
        }
        case consts.SHAPES.CIRCLE: {
          return (
            <Ellipse
              id={GhostElementId}
              radiusX={radius * scaleX}
              radiusY={radius * scaleY}
              name="circle"
              fill={color}
              opacity={0.2}
              shadowBlur={0}
              stroke={border}
              strokeWidth={defaultShapeStrokeWidth}
              x={centerX}
              y={centerY}
            />
          );
        }
        case consts.SHAPES.RECT_ROUNDED: {
          return (
            <Rect
              id={GhostElementId}
              cornerRadius={consts.DEFAULTS.ROUNDED_RECT_RADIUS}
              width={width}
              height={height}
              name="rect-rounded"
              fill={color}
              opacity={0.2}
              shadowBlur={0}
              stroke={border}
              strokeWidth={defaultShapeStrokeWidth}
              x={centerX - width / 2}
              y={centerY - height / 2}
            />
          );
        }
        case consts.SHAPES.TRIANGLE: {
          return (
            <Group
              id={GhostElementId}
              name="triangle"
              x={centerX}
              y={centerY}
              opacity={0.2}
              scaleX={scaleX}
              scaleY={scaleY}
            >
              <PolygonElement
                id={GhostElementId}
                sides={3}
                scaleX={width / (radius * 2)}
                scaleY={height / (radius * 2)}
                radius={radius}
                fill={color}
                stroke={border}
                strokeWidth={defaultShapeStrokeWidth}
                strokeEnabled={border != "transparent"}
              />
            </Group>
          );
        }
        case consts.SHAPES.DIAMOND: {
          return (
            <Group
              id={GhostElementId}
              name="diamond"
              x={centerX}
              y={centerY}
              opacity={0.2}
              scaleX={scaleX}
              scaleY={scaleY}
            >
              <PolygonElement
                id={GhostElementId}
                sides={4}
                scaleX={width / (radius * 2)}
                scaleY={height / (radius * 2)}
                radius={radius}
                fill={color}
                stroke={border}
                strokeWidth={defaultShapeStrokeWidth}
                strokeEnabled={border != "transparent"}
              />
            </Group>
          );
        }
        case consts.SHAPES.HEXAGON: {
          return (
            <Group
              id={GhostElementId}
              name="hexagon"
              x={centerX}
              y={centerY}
              opacity={0.2}
              scaleX={scaleX}
              scaleY={scaleY}
            >
              <PolygonElement
                id={GhostElementId}
                sides={6}
                scaleX={width / (radius * 2)}
                scaleY={height / (radius * 2)}
                radius={radius}
                fill={color}
                stroke={border}
                strokeWidth={defaultShapeStrokeWidth}
                strokeEnabled={border != "transparent"}
              />
            </Group>
          );
        }
        default: {
          // Throws build error if not all cases are handled in the switch
          const _unhandled: never = type;
          throw new Error(`Unhandled shape type: ${type}`);
        }
      }
    }
  }

  function renderGhostConnector() {
    // TODO: when we have color and shape in the newElementData, use them here !
    // If the line has length 0 we just draw a small circle
    if (point1.x == point2.x && point1.y == point2.y)
      return <Circle x={point1.x} y={point1.y} radius={5} opacity={0.2} fill={"black"} listening={false} />;

    const element: ConnectorInput = {
      x: point1.x,
      y: point1.y,
      type: "connector",
      lineType: data.connector.lineType,
      connectedShapes: [
        { id: data.connector.start?.shapeId ?? "", type: "" },
        { id: data.connector.end?.shapeId ?? "", type: "" },
      ],
      rotate: 0,
      points: [point1, point2],
      anchorMode: data.connector.start.side ?? null,
      anchorsMode: [data.connector.start.side ?? "n/a", data.connector.end.side ?? "n/a"],
      point1_t: data.connector.start?.t ?? 0,
      point2_t: data.connector.end?.t ?? 0,
      anchorOrientation: null,
      anchorIndexes: [1, 0],
      activeAnchorIndex: undefined,
      stroke: data.connector.stroke ?? (isCIM ? "#A6AAAD" : consts.COLOR_PALETTE[0]),
      strokeWidth: data.connector.strokeWidth ?? Sizes.get().connector.strokeWidth.default,
      dash: data.connector.dash,
      pointerStyles: [data.connector.endArrow, data.connector.startArrow],
      zIndexLastChangeTime: 0,
      text: "",
      fontSize: 14,
      cursorPosition: 0,
      align: "left",
      fontProps: 0,
      font: "poppins",
    };

    return (
      <GhostConnector
        id={GhostElementId}
        p1={point1 as any}
        p2={point2 as any}
        lineType={data.connector.lineType}
        element={element}
      />
    );
  }

  function renderGhostCard() {
    const color = "#8f8f8f";
    return (
      <Rect
        id={GhostElementId}
        x={point1.x}
        y={point1.y}
        width={consts.DEFAULTS.CARD_WIDTH}
        height={consts.DEFAULTS.CARD_HEIGHT}
        stroke={color}
        strokeWidth={1.5}
        cornerRadius={4}
        shadowOffsetY={3.23}
        shadowBlur={2.58}
        shadowColor={"#6F7284"}
        shadowOpacity={0.15}
        opacity={0.5}
        fillLinearGradientStartPointY={0}
        fillLinearGradientEndPointY={15}
        fillLinearGradientColorStops={[0, color, 0.99, color, 1, "white"]}
      />
    );
  }

  function renderGhostMindMap() {
    const { width, height } = sizes.mindmap.rootSize;
    return (
      <Group id={GhostElementId} x={point1.x - width / 2} y={point1.y - height / 2} opacity={0.5}>
        <Rect width={width} height={height} stroke={"#000"} strokeWidth={2} cornerRadius={height / 2} />
        <Text width={width} height={height} align="center" verticalAlign="middle" text="Mind Map" />
      </Group>
    );
  }

  function renderGhostFrame(width: number | null, height: number | null, color?: string) {
    const frameWidth = width ?? Math.abs(point1.x - point2.x);
    const frameHeight = height ?? Math.abs(point1.y - point2.y);
    const x = Math.min(point1.x, point2.x);
    const y = Math.min(point1.y, point2.y);
    return (
      <Rect
        id={GhostElementId}
        x={x}
        y={y}
        width={frameWidth}
        height={frameHeight}
        stroke={"#DADCE0"}
        fill={color ?? "#FFFFFF"}
        opacity={0.33}
      />
    );
  }

  function renderGhostCardStack() {
    return (
      <Group opacity={0.5}>
        <CardStackFrame x={point1.x} y={point1.y} />
      </Group>
    );
  }

  function renderGhostTimeline() {
    return (
      <Group opacity={0.5}>
        <Rect
          x={point1.x}
          y={point1.y}
          width={Math.abs(point1.x - point2.x)}
          height={2}
          fill={"#000"}
          stroke={"#000"}
          strokeWidth={2}
        />
      </Group>
    );
  }

  function renderGhostGantt() {
    const controller = new GanttGhostController(createEmptyContext({}));
    return (
      <Group id={GhostElementId} x={point1.x} y={point1.y}>
        <GanttElementComponent id={GhostElementId} controller={controller} />
      </Group>
    );
  }

  function renderGhostTable() {
    const { rows, cols } = data.table;
    const { x, y } = point1;
    const width = Math.abs(point2.x - x);
    const height = Math.abs(point2.y - y);
    const cellWidth = width == 0 ? sizes.table.cellWidth : width / cols;
    const cellHeight = height == 0 ? sizes.table.cellHeight : height / rows;
    return (
      <Group opacity={0.5} x={x} y={y}>
        <GhostTable
          cols={cols}
          rows={rows}
          cellWidth={cellWidth}
          cellHeight={cellHeight}
          initialStyle={data.table.style}
          borderColor={data.table.style.stroke}
        />
      </Group>
    );
  }

  function renderGhostNounIcon() {
    return (
      <Group opacity={0.5} x={point1.x} y={point1.y}>
        <GhostNounIcon url={data.nounIcon.thumbnail} />
      </Group>
    );
  }

  function renderGhostChart() {
    const controller = new ChartGhostController(data.chartType);
    return (
      <Group x={point1.x} y={point1.y}>
        <ChartElementComponent id={"dummy"} controller={controller} />
      </Group>
    );
  }

  function renderGhostIcon() {
    const controller = new IconController("", data.icon, createEmptyContext({}), false);
    return (
      <Group opacity={0.5} x={point1.x} y={point1.y} scaleX={data.icon?.scaleX ?? 1} scaleY={data.icon?.scaleY ?? 1}>
        <IconElementComponent id={"dummy"} controller={controller} />
      </Group>
    );
  }

  function renderGhostTask() {
    const controller = new TaskController(GhostElementId, data.task, createEmptyContext({}));
    return (
      <Group x={point1.x} y={point1.y}>
        <TaskElementComponent id={"dummy"} controller={controller} />
      </Group>
    );
  }

  function renderGhostByElementType() {
    switch (elementType) {
      case consts.CANVAS_ELEMENTS.STICKY_NOTE: {
        const {
          color,
          width = sizes.stickyNote.height,
          height = sizes.stickyNote.height,
          scaleX = 1,
          scaleY = 1,
        } = data.stickyNote;

        return (
          <Rect
            id={GhostElementId}
            width={(width + sizes.stickyNote.marginX) * scaleX}
            height={(height + sizes.stickyNote.marginY) * scaleY}
            fill={color}
            perfectDrawEnabled={false}
            opacity={0.4}
            x={centerX - (width * scaleX) / 2}
            y={centerY - (height * scaleY) / 2}
          />
        );
      }
      case consts.CANVAS_ELEMENTS.SHAPE: {
        return renderGhostShapeByShapeType();
      }
      case consts.CANVAS_ELEMENTS.CONNECTOR: {
        return renderGhostConnector();
      }
      case consts.CANVAS_ELEMENTS.TASK_CARD: {
        return renderGhostCard();
      }
      case consts.CANVAS_ELEMENTS.FRAME: {
        const { width, height, color } = data.frame;
        return renderGhostFrame(width, height, color);
      }
      case consts.CANVAS_ELEMENTS.MINDMAP: {
        return renderGhostMindMap();
      }
      case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
        return renderGhostMindMap();
      }
      case consts.CANVAS_ELEMENTS.CARD_STACK: {
        return renderGhostCardStack();
      }
      case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION: {
        return renderGhostCardStack();
      }
      case consts.CANVAS_ELEMENTS.TIMELINE: {
        return renderGhostTimeline();
      }
      case consts.CANVAS_ELEMENTS.TABLE: {
        return renderGhostTable();
      }
      case consts.CANVAS_ELEMENTS.GANTT: {
        return renderGhostGantt();
      }
      case consts.CANVAS_ELEMENTS.NOUN_ICON: {
        return renderGhostNounIcon();
      }
      case consts.CANVAS_ELEMENTS.CHART: {
        return renderGhostChart();
      }
      case consts.CANVAS_ELEMENTS.ICON: {
        return renderGhostIcon();
      }
      case consts.CANVAS_ELEMENTS.TASK: {
        return renderGhostTask();
      }
      default: {
        return null;
      }
    }
  }
  return <React.Fragment>{renderGhostByElementType()}</React.Fragment>;
}
